/** @jsx jsx */
import type {
  CodeEditorTheme,
  ConsoleProps,
  ConsoleRef,
  SupportedEditorLanguages,
} from '@datacamp/code-editor';
import { Console } from '@datacamp/code-editor';
import { tokens } from '@datacamp/waffles/tokens';
import { jsx } from '@emotion/react';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { useDispatch, useSelector } from '../../interfaces/State';
import type { SessionOutput } from '../../redux/actions';
import * as actions from '../../redux/actions';
import * as selectors from '../../redux/selectors';
import useAutoCompletionProviders from '../CodeEditor/useAutoCompletionProviders';
import Reconnecter from '../Reconnecter';

import useConsoleShortcuts from './useConsoleShortcuts';

type Props = {
  language: SupportedEditorLanguages;
  uiTheme: 'DARK' | 'LIGHT';
};
export type MonacoConsoleProps = Props;

const MonacoConsole: React.FC<Props> = ({ language, uiTheme }) => {
  const dispatch = useDispatch();
  const exercise = useSelector((state) =>
    selectors.selectExercise(state).toJS(),
  );
  const {
    id,
    pre_exercise_code: preExerciseCode,
    sct,
    solution,
    type,
  } = exercise;
  const isSessionBroken = useSelector(selectors.isSessionBroken);
  const isSessionReady = useSelector(selectors.isSessionReady);
  const multiplexerOutputs = useSelector(selectors.selectOutput);
  const consoleRef = useRef<ConsoleRef>(null);
  const autoCompletionProviders = useAutoCompletionProviders({
    pec: preExerciseCode,
    sct,
    solution,
    type,
    language,
  });
  const theme = useMemo(
    () => uiTheme.toLowerCase() as CodeEditorTheme,
    [uiTheme],
  );
  const onClearConsole = useCallback(() => {
    dispatch(actions.clearConsoleOutput());
  }, [dispatch]);
  const outputsCallback = useCallback(
    (outputs: SessionOutput[]) => {
      dispatch(actions.setConsoleOutput(outputs));
    },
    [dispatch],
  );
  const onExecuteCommand: ConsoleProps['onExecuteCommand'] = useCallback(
    ({ command, commandType }) => {
      dispatch(
        actions.executeCode({
          code: command,
          command: commandType,
          pec: preExerciseCode,
          type,
          sct,
          solution,
          language,
          id,
        }),
      );
    },
    [dispatch, id, language, preExerciseCode, sct, solution, type],
  );

  useConsoleShortcuts({
    focus: consoleRef.current == null ? undefined : consoleRef.current.focus,
  });
  useEffect(() => {
    dispatch(actions.registerConsoleOutputCallback(outputsCallback));
  }, [outputsCallback, dispatch]);

  useEffect(() => {
    if (isSessionBroken) {
      dispatch(actions.clearConsoleOutput());
    }
  }, [dispatch, isSessionBroken]);

  const hintAndSolution = useSelector(selectors.selectHintOrSolution);
  const exerciseId = useSelector(selectors.selectExercise).get('id');
  const isSolutionShown = useSelector(selectors.selectExercise).getIn(
    ['user', 'isSolutionShown'],
    false,
  );
  const subExerciseId = useSelector(selectors.selectCurrentSubExercise).get(
    'id',
  );
  const currentExerciseId = subExerciseId != null ? subExerciseId : exerciseId;
  const { xp } = hintAndSolution;

  useEffect(() => {
    dispatch(actions.clearConsoleOutput());
  }, [dispatch, currentExerciseId]);

  const onTakeHintOrSolution = useCallback(() => {
    if (!hintAndSolution.isSolution) {
      dispatch(actions.showHint(currentExerciseId, xp));
    } else {
      if (isSolutionShown) {
        return;
      }
      dispatch(actions.showSolution(language, currentExerciseId, xp));
    }
  }, [
    currentExerciseId,
    dispatch,
    hintAndSolution.isSolution,
    isSolutionShown,
    language,
    xp,
  ]);

  return (
    <div
      css={{
        height: '100%',
      }}
      data-cy="console-editor"
    >
      <Console
        css={{
          borderBottomLeftRadius: tokens.borderRadius.medium,
          borderBottomRightRadius: tokens.borderRadius.medium,
        }}
        multiplexerOutputs={multiplexerOutputs}
        ref={consoleRef}
        autoCompletionProviders={autoCompletionProviders}
        language={language}
        theme={theme}
        exercise={exercise}
        onExecuteCommand={onExecuteCommand}
        onTakeHintOrSolution={onTakeHintOrSolution}
        onClearConsole={onClearConsole}
        disableCommandExecution={!isSessionReady}
      />
      <Reconnecter />
    </div>
  );
};

export default MonacoConsole;
